import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PlusIcon, MagnifyingGlassCircleIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import UserModal from '../modals/UserModal';
import API_URL from '../config/apiConfig';

const UsersPage = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);

    // Pour la pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [usersPerPage] = useState(10);

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            // Dans un environnement réel, cette requête devrait inclure un token d'authentification
            const response = await axios.get(`${API_URL}/admin/users`, {
                params: { adminKey: process.env.REACT_APP_ADMIN_KEY }
            });

            if (response.data.success) {
                setUsers(response.data.data.users);
            } else {
                console.error('Erreur lors de la récupération des utilisateurs:', response.data.message);
            }
        } catch (error) {
            console.error('Erreur lors de la récupération des utilisateurs:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddUser = () => {
        setCurrentUser(null); // Reset pour un nouvel utilisateur
        setShowModal(true);
    };

    const handleEditUser = (user) => {
        setCurrentUser(user);
        setShowModal(true);
    };

    const handleSaveUser = async (userData) => {
        try {
            if (currentUser) {
                // Mise à jour d'un utilisateur existant
                await axios.put(`${API_URL}/admin/users/${userData.id}`, {
                    ...userData,
                    adminKey: process.env.REACT_APP_ADMIN_KEY
                });
            } else {
                // Création d'un nouvel utilisateur
                await axios.post(`${API_URL}/admin/users`, {
                    ...userData,
                    adminKey: process.env.REACT_APP_ADMIN_KEY
                });
            }

            setShowModal(false);
            fetchUsers(); // Actualiser la liste
        } catch (error) {
            console.error('Erreur lors de la sauvegarde:', error);
            // Ici, vous pourriez afficher un message d'erreur à l'utilisateur
        }
    };

    // Filtrage par recherche
    const filteredUsers = users.filter(user =>
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Pagination
    const indexOfLastUser = currentPage * usersPerPage;
    const indexOfFirstUser = indexOfLastUser - usersPerPage;
    const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
    const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

    // Fonction pour changer de page
    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    return (
        <div>
            <div className="flex justify-between mb-6">
                <h1 className="text-2xl font-semibold text-gray-700">Gestion des utilisateurs</h1>
                <button
                    onClick={handleAddUser}
                    className="flex items-center px-4 py-2 bg-[#E48B94] text-white rounded-md hover:bg-[#d57a84] focus:outline-none"
                >
                    <PlusIcon className="w-5 h-5 mr-2" />
                    Ajouter un utilisateur
                </button>
            </div>

            {/* Barre de recherche */}
            <div className="relative mb-6">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <MagnifyingGlassCircleIcon className="w-5 h-5 text-gray-400" />
                </div>
                <input
                    type="text"
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Rechercher un utilisateur..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            {/* Tableau des utilisateurs */}
            <div className="overflow-x-auto bg-white rounded-lg shadow">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                    <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Nom
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Email
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Générations PDF utilisées
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Quota mensuel
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date d'inscription
                        </th>
                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                        </th>
                    </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                    {loading ? (
                        <tr>
                            <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                                Chargement...
                            </td>
                        </tr>
                    ) : currentUsers.length > 0 ? (
                        currentUsers.map((user) => (
                            <tr key={user.id}>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900">{user.name}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-500">{user.email}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-500">{user.pdfGenerationCount}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-500">{user.pdfMonthlyQuota}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-500">
                                        {new Date(user.createdAt).toLocaleDateString()}
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <button
                                        onClick={() => handleEditUser(user)}
                                        className="text-indigo-600 hover:text-indigo-900 mr-3"
                                    >
                                        <PencilIcon className="w-5 h-5" />
                                    </button>
                                    <button
                                        className="text-red-600 hover:text-red-900"
                                    >
                                        <TrashIcon className="w-5 h-5" />
                                    </button>
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                                Aucun utilisateur trouvé
                            </td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {!loading && filteredUsers.length > usersPerPage && (
                <div className="flex justify-center mt-6">
                    <nav>
                        <ul className="flex">
                            <li>
                                <button
                                    onClick={() => paginate(currentPage > 1 ? currentPage - 1 : 1)}
                                    disabled={currentPage === 1}
                                    className={`px-3 py-1 rounded-l-md ${
                                        currentPage === 1 ? 'bg-gray-200 text-gray-500' : 'bg-[#3D5A80] text-white hover:bg-[#2b3f59]'
                                    }`}
                                >
                                    Précédent
                                </button>
                            </li>
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
                                <li key={number}>
                                    <button
                                        onClick={() => paginate(number)}
                                        className={`px-3 py-1 ${
                                            currentPage === number ? 'bg-[#E48B94] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                        }`}
                                    >
                                        {number}
                                    </button>
                                </li>
                            ))}
                            <li>
                                <button
                                    onClick={() => paginate(currentPage < totalPages ? currentPage + 1 : totalPages)}
                                    disabled={currentPage === totalPages}
                                    className={`px-3 py-1 rounded-r-md ${
                                        currentPage === totalPages ? 'bg-gray-200 text-gray-500' : 'bg-[#3D5A80] text-white hover:bg-[#2b3f59]'
                                    }`}
                                >
                                    Suivant
                                </button>
                            </li>
                        </ul>
                    </nav>
                </div>
            )}

            {/* Modal pour ajouter/modifier un utilisateur */}
            {showModal && (
                <UserModal
                    user={currentUser}
                    onClose={() => setShowModal(false)}
                    onSave={handleSaveUser}
                />
            )}
        </div>
    );
};

export default UsersPage;